import { DialogCloseEvent } from './dialog.js';
import { clamp, preventDefault, isMobileBreakpoint } from './utilities.js';
import { Component } from '@theme/component';

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
/**
 * How much a click or a double-tap zooms in, relative to the size the image rests at. Relative
 * rather than absolute, so the step feels the same on desktop (which rests magnified) as it does
 * on touch (which rests fitted).
 */
const CLICK_ZOOM_FACTOR = 2;
/**
 * The zoom every image opens at on desktop, so the zoom reads as a zoom rather than as a larger
 * copy of the gallery image — scrolling on to the next image shows it magnified too. Touch opens
 * fitted throughout, where the screen is already filled by the product.
 */
const DESKTOP_INITIAL_ZOOM = 1.6;
const DOUBLE_TAP_DELAY = 300;
const DOUBLE_TAP_DISTANCE = 50;
const DRAG_THRESHOLD = 10;

/**
 * @typedef {object} Refs
 * @property {HTMLImageElement} image - The image element to zoom and drag.
 */

/**
 * Zoom and pan behaviour for a product image inside the zoom dialog.
 *
 * The image opens fitted to the viewport. Clicking it (mouse) or double-tapping it (touch) toggles
 * between the fitted view and TOGGLE_ZOOM, anchored on the point the customer aimed at; dragging
 * pans the zoomed image, and pinching zooms freely between MIN_ZOOM and MAX_ZOOM. Desktop and
 * touch therefore behave the same way, with the gesture each input supports.
 *
 * @extends {Component<Refs>}
 */
export class DragZoomWrapper extends Component {
  requiredRefs = ['image'];
  #controller = new AbortController();
  /** @type {number} */
  #scale = MIN_ZOOM;
  /** @type {number} */
  #initialDistance = 0;
  /** @type {number} */
  #startScale = MIN_ZOOM;
  /** @type {Point} */
  #translate = { x: 0, y: 0 };
  /** @type {Point} */
  #startPosition = { x: 0, y: 0 };
  /** @type {Point} */
  #startTranslate = { x: 0, y: 0 };
  /** @type {boolean} */
  #isDragging = false;
  /** @type {boolean} */
  #initialized = false;
  /** @type {number | null} */
  #animationFrame = null;
  /** @type {number} */
  #lastTapTime = 0;
  /** @type {Point | null} */
  #lastTapPosition = null;

  /** @type {boolean} */
  #hasDraggedBeyondThreshold = false;

  /** @type {number | null} */
  #activePointerId = null;

  get #image() {
    return this.refs.image;
  }

  /**
   * The zoom an image rests at: magnified on desktop, fitted on touch. Applied when the component
   * connects and whenever the dialog closes, so every image in the gallery opens magnified rather
   * than only the one that was clicked.
   */
  get #initialScale() {
    return isMobileBreakpoint() ? MIN_ZOOM : DESKTOP_INITIAL_ZOOM;
  }

  connectedCallback() {
    super.connectedCallback();
    if (!this.#image) return;

    this.#initResizeListener();
    window.addEventListener(DialogCloseEvent.eventName, this.#resetZoom);

    this.#initEventListeners();
    this.#resetZoom();
  }

  #initResizeListener() {
    this.#resizeObserver.observe(this);
  }

  #initEventListeners() {
    if (this.#initialized) return;
    this.#initialized = true;
    const { signal } = this.#controller;
    const options = { passive: false, signal };

    this.addEventListener('touchstart', this.#handleTouchStart, options);
    this.addEventListener('touchmove', this.#handleTouchMove, options);
    this.addEventListener('touchend', this.#handleTouchEnd, options);

    // Mouse input is handled through pointer events; touch input keeps using the touch events
    // above, so pointer handlers ignore anything that is not a mouse.
    this.addEventListener('pointerdown', this.#handlePointerDown, { signal });
    this.addEventListener('pointermove', this.#handlePointerMove, { signal });
    this.addEventListener('pointerup', this.#handlePointerUp, { signal });
    this.addEventListener('pointercancel', this.#handlePointerUp, { signal });
    this.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener('dragstart', preventDefault, { signal });

    // Initialize transform immediately
    this.#updateTransform();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener(DialogCloseEvent.eventName, this.#resetZoom);
    this.#controller.abort();
    this.#resizeObserver.disconnect();
    this.#cancelAnimationFrame();
  }

  #handleResize = () => {
    this.#requestUpdateTransform();
  };

  #resizeObserver = new ResizeObserver(this.#handleResize);

  /**
   * Toggle the flag that suppresses the zoom easing, so the image follows the finger or the
   * cursor directly while a drag or a pinch is in progress.
   * @param {boolean} interacting
   */
  #setInteracting(interacting) {
    this.toggleAttribute('interacting', interacting);
  }

  /**
   * @param {TouchEvent} event
   */
  #handleTouchStart = (event) => {
    preventDefault(event);

    const touchCount = event.touches.length;

    if (touchCount === 2) {
      // Early exit if touches are invalid
      const touch1 = event.touches[0];
      const touch2 = event.touches[1];
      if (!touch1 || !touch2) return;

      // Avoid object allocation by passing touches directly
      this.#startZoomGestureFromTouches(touch1, touch2);
    } else if (touchCount === 1) {
      const touch = event.touches[0];
      if (!touch) return;

      // Use performance.now() for better precision and performance
      const currentTime = performance.now();
      const timeSinceLastTap = currentTime - this.#lastTapTime;

      // Early exit if too much time has passed
      if (timeSinceLastTap >= DOUBLE_TAP_DELAY) {
        this.#storeTapInfo(currentTime, touch);
        this.#startDragGestureFromTouch(touch);
        return;
      }

      // Only check distance if we have a previous tap within time window
      if (this.#lastTapPosition) {
        // Distance calculation with early exit
        const distance = getDistance(touch, this.#lastTapPosition);

        if (distance < DOUBLE_TAP_DISTANCE) {
          // This is a double-tap, handle zoom toggle
          this.#toggleZoom({ x: touch.clientX, y: touch.clientY });
          this.#lastTapTime = 0; // Reset to prevent triple-tap
          this.#lastTapPosition = null;
          return;
        }
      }

      // Store tap info for potential double-tap detection
      this.#storeTapInfo(currentTime, touch);
      this.#startDragGestureFromTouch(touch);
    }
  };

  /**
   * Start a zoom gesture with two touches
   * @param {Touch} touch1
   * @param {Touch} touch2
   */
  #startZoomGestureFromTouches(touch1, touch2) {
    // Calculate initial distance between touches
    this.#initialDistance = getDistance(touch1, touch2);
    this.#startScale = this.#scale;
    this.#isDragging = false;
  }

  /**
   * Start a drag gesture with a single touch
   * @param {Touch} touch
   */
  #startDragGestureFromTouch(touch) {
    this.#startPosition = { x: touch.clientX, y: touch.clientY };
    this.#startTranslate = { x: this.#translate.x, y: this.#translate.y };
    this.#isDragging = true;
    this.#hasDraggedBeyondThreshold = false;
  }

  /**
   * Store tap information for double-tap detection
   * @param {number} currentTime
   * @param {Touch} touch
   */
  #storeTapInfo(currentTime, touch) {
    this.#lastTapTime = currentTime;
    this.#lastTapPosition = { x: touch.clientX, y: touch.clientY };
  }

  /**
   * Toggle between the size the image rests at and one step closer, keeping the point the
   * customer aimed at in place as the image grows.
   * @param {Point} point - The viewport coordinates the zoom is anchored on.
   */
  #toggleZoom(point) {
    const restingScale = this.#initialScale;

    if (this.#scale > restingScale) {
      // Zoomed in: go back to the size the image opened at. Returning to the fitted size instead
      // made a second click shrink the product well below what the customer started with.
      this.#scale = restingScale;
      this.#translate = { x: 0, y: 0 };
      this.#requestUpdateTransform();
      return;
    }

    const center = this.#getCenter();
    const oldScale = this.#scale;

    this.#scale = clamp(restingScale * CLICK_ZOOM_FACTOR, MIN_ZOOM, MAX_ZOOM);

    const scaleDelta = this.#scale / oldScale - 1;

    this.#translate.x -= ((point.x - center.x) * scaleDelta) / this.#scale;
    this.#translate.y -= ((point.y - center.y) * scaleDelta) / this.#scale;

    this.#requestUpdateTransform();
  }

  /**
   * The centre of the wrapper, in viewport coordinates.
   * @returns {Point}
   */
  #getCenter() {
    const rect = this.getBoundingClientRect();

    return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
  }

  /**
   * Whether a point is over the picture itself rather than the empty space beside it. The image
   * element fills the stage and the picture is letterboxed inside it by `object-fit: contain`,
   * so the element's own box is too generous to test against.
   *
   * @param {Point} point - The viewport coordinates to test.
   * @returns {boolean}
   */
  #isPointOverImage(point) {
    if (!this.#image) return false;

    const rect = this.#image.getBoundingClientRect();
    const { naturalWidth, naturalHeight } = this.#image;

    let { left, right, top, bottom } = rect;

    if (naturalWidth > 0 && naturalHeight > 0) {
      const scale = Math.min(rect.width / naturalWidth, rect.height / naturalHeight);
      const letterboxX = (rect.width - naturalWidth * scale) / 2;
      const letterboxY = (rect.height - naturalHeight * scale) / 2;

      left += letterboxX;
      right -= letterboxX;
      top += letterboxY;
      bottom -= letterboxY;
    }

    return point.x >= left && point.x <= right && point.y >= top && point.y <= bottom;
  }

  /**
   * @param {TouchEvent} event
   */
  #handleTouchMove = (event) => {
    preventDefault(event);

    const touchCount = event.touches.length;

    if (touchCount === 2) {
      const touch1 = event.touches[0];
      const touch2 = event.touches[1];
      if (touch1 && touch2) {
        this.#processZoomGesture(touch1, touch2);
      }
    } else if (touchCount === 1 && this.#isDragging) {
      const touch = event.touches[0];
      if (touch) {
        this.#processDragGesture(touch);
      }
    }
  };

  /**
   * Process zoom gesture from touches
   * @param {Touch} touch1
   * @param {Touch} touch2
   */
  #processZoomGesture(touch1, touch2) {
    this.#setInteracting(true);

    // Calculate midpoint directly without object allocation
    const midX = (touch1.clientX + touch2.clientX) / 2;
    const midY = (touch1.clientY + touch2.clientY) / 2;

    // Calculate current distance between touches
    const currentDistance = getDistance(touch1, touch2);

    const oldScale = this.#scale;

    // Calculate and apply new scale
    const newScale = (currentDistance / this.#initialDistance) * this.#startScale;
    this.#scale = clamp(newScale, MIN_ZOOM, MAX_ZOOM);

    // Adjust translation to keep the pinch midpoint stationary
    const center = this.#getCenter();

    // Calculate how the image position needs to change to keep the midpoint stationary
    const scaleDelta = this.#scale / oldScale - 1.0;

    // Apply correction to prevent zooming on the opposite side of the midpoint
    this.#translate.x -= ((midX - center.x) * scaleDelta) / this.#scale;
    this.#translate.y -= ((midY - center.y) * scaleDelta) / this.#scale;

    this.#requestUpdateTransform();
    this.#isDragging = false;
  }

  /**
   * Process drag gesture from touch
   * @param {Touch} touch
   */
  #processDragGesture(touch) {
    // Check if we've moved beyond the drag threshold
    const distance = getDistance(touch, this.#startPosition);

    if (!this.#hasDraggedBeyondThreshold && distance < DRAG_THRESHOLD) {
      // Movement is too small, don't process as drag yet
      return;
    }

    this.#hasDraggedBeyondThreshold = true;
    this.#setInteracting(true);

    // Calculate movement deltas for translation
    const dx = touch.clientX - this.#startPosition.x;
    const dy = touch.clientY - this.#startPosition.y;

    // Calculate new translation directly
    this.#translate.x = this.#startTranslate.x + dx / this.#scale;
    this.#translate.y = this.#startTranslate.y + dy / this.#scale;

    this.#requestUpdateTransform();
  }

  /**
   * @param {TouchEvent} event
   */
  #handleTouchEnd = (event) => {
    if (event.touches.length === 0) {
      this.#isDragging = false;
      this.#setInteracting(false);
      this.#requestUpdateTransform();

      this.#hasDraggedBeyondThreshold = false;
    }
  };

  /**
   * @param {PointerEvent} event
   */
  #handlePointerDown = (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;

    this.#startPosition = { x: event.clientX, y: event.clientY };
    this.#startTranslate = { x: this.#translate.x, y: this.#translate.y };
    this.#hasDraggedBeyondThreshold = false;

    // Only a zoomed-in image can be panned; otherwise the press is just the start of a click.
    if (this.#scale <= MIN_ZOOM) return;

    this.#isDragging = true;
    this.#activePointerId = event.pointerId;
    this.setPointerCapture(event.pointerId);
  };

  /**
   * @param {PointerEvent} event
   */
  #handlePointerMove = (event) => {
    if (event.pointerType !== 'mouse' || !this.#isDragging) return;

    const dx = event.clientX - this.#startPosition.x;
    const dy = event.clientY - this.#startPosition.y;

    if (!this.#hasDraggedBeyondThreshold && Math.sqrt(dx * dx + dy * dy) < DRAG_THRESHOLD) return;

    this.#hasDraggedBeyondThreshold = true;
    this.#setInteracting(true);

    this.#translate.x = this.#startTranslate.x + dx / this.#scale;
    this.#translate.y = this.#startTranslate.y + dy / this.#scale;

    this.#requestUpdateTransform();
  };

  /**
   * @param {PointerEvent} event
   */
  #handlePointerUp = (event) => {
    if (event.pointerType !== 'mouse') return;

    if (this.#activePointerId !== null && this.hasPointerCapture(this.#activePointerId)) {
      this.releasePointerCapture(this.#activePointerId);
    }

    this.#activePointerId = null;
    this.#isDragging = false;
    this.#setInteracting(false);
    this.#requestUpdateTransform();
  };

  /**
   * Whether this click is the wrapper's to handle, rather than a click that should close the
   * dialog. The zoom dialog asks this before closing, because the theme's declarative `on:click`
   * handlers run in the capture phase and cannot be stopped from here.
   *
   * @param {MouseEvent} event - The click event.
   * @returns {boolean} True when the click zooms, or ends a pan.
   */
  handlesClick(event) {
    // The click that ends a pan is not a click on the image.
    if (this.#hasDraggedBeyondThreshold) return true;

    // Zoomed in past the resting size, any click zooms back out.
    if (this.#scale > this.#initialScale) return true;

    // At the resting size, only a click on the picture itself zooms; beside it the dialog closes.
    return this.#isPointOverImage({ x: event.clientX, y: event.clientY });
  }

  /**
   * Toggles the zoom on click.
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    if (this.#hasDraggedBeyondThreshold) {
      this.#hasDraggedBeyondThreshold = false;
      return;
    }

    if (!this.handlesClick(event)) return;

    this.#toggleZoom({ x: event.clientX, y: event.clientY });
  };

  /**
   * Calculate how far the image may be translated in each direction at the current zoom level.
   * @returns {Point} The maximum translation, in the same units as `#translate`.
   */
  #getMaxTranslate() {
    const containerWidth = this.clientWidth;
    const containerHeight = this.clientHeight;

    if (!containerWidth || !containerHeight || !this.#image || this.#scale <= MIN_ZOOM) {
      return { x: 0, y: 0 };
    }

    // Get wrapper dimensions
    const wrapperRect = this.getBoundingClientRect();

    // Calculate ACTUAL image content dimensions at current zoom
    // The image element may fill the wrapper, but the content has its own aspect ratio
    const imageElement = this.#image;
    let naturalWidth, naturalHeight;

    // Try to get natural dimensions
    if (imageElement.naturalWidth > 0 && imageElement.naturalHeight > 0) {
      naturalWidth = imageElement.naturalWidth;
      naturalHeight = imageElement.naturalHeight;
    } else {
      // Fallback: assume square image if we can't get natural dimensions
      naturalWidth = wrapperRect.width;
      naturalHeight = wrapperRect.width;
    }

    // Calculate how the image fits within the wrapper (object-fit: contain behavior)
    const imageAspectRatio = naturalWidth / naturalHeight;
    const wrapperAspectRatio = wrapperRect.width / wrapperRect.height;

    let actualImageWidth, actualImageHeight;

    if (imageAspectRatio > wrapperAspectRatio) {
      // Image is wider - width fits exactly, height is smaller (letterboxed top/bottom)
      actualImageWidth = wrapperRect.width;
      actualImageHeight = actualImageWidth / imageAspectRatio;
    } else {
      // Image is taller - height fits exactly, width is smaller (letterboxed left/right)
      actualImageHeight = wrapperRect.height;
      actualImageWidth = actualImageHeight * imageAspectRatio;
    }

    // Apply current zoom scale
    const scaledImageWidth = actualImageWidth * this.#scale;
    const scaledImageHeight = actualImageHeight * this.#scale;

    const horizontalOverflow = Math.max(0, scaledImageWidth - wrapperRect.width);
    const verticalOverflow = Math.max(0, scaledImageHeight - wrapperRect.height);

    // Max translation is half the overflow (since the image starts centred), so it can never be
    // dragged past its own edge.
    return {
      x: horizontalOverflow / 2 / this.#scale,
      y: verticalOverflow / 2 / this.#scale,
    };
  }

  /**
   * Constrain image translation to keep it within the viewport
   */
  #constrainTranslation() {
    // Keep scale between MIN_ZOOM (1) and MAX_ZOOM (5)
    this.#scale = clamp(this.#scale, MIN_ZOOM, MAX_ZOOM);

    const { x: maxTranslateX, y: maxTranslateY } = this.#getMaxTranslate();

    this.#translate.x = clamp(this.#translate.x, -maxTranslateX, maxTranslateX);
    this.#translate.y = clamp(this.#translate.y, -maxTranslateY, maxTranslateY);
  }

  /**
   * Request an animation frame to update the transform
   */
  #requestUpdateTransform = () => {
    if (!this.#animationFrame) {
      this.#animationFrame = requestAnimationFrame(this.#updateTransform);
    }
  };

  /**
   * Cancel any pending animation frame
   */
  #cancelAnimationFrame() {
    if (this.#animationFrame) {
      cancelAnimationFrame(this.#animationFrame);
      this.#animationFrame = null;
    }
  }

  #updateTransform = () => {
    this.#animationFrame = null;

    this.#constrainTranslation();
    this.style.setProperty('--drag-zoom-scale', this.#scale.toString());
    this.style.setProperty('--drag-zoom-translate-x', `${this.#translate.x}px`);
    this.style.setProperty('--drag-zoom-translate-y', `${this.#translate.y}px`);

    // Drives the zoom-in / grab cursors
    this.toggleAttribute('zoomed', this.#scale > MIN_ZOOM);
  };

  /**
   * Reset zoom to the fitted, centred state. Called when the zoom dialog is closed, so the next
   * image always opens showing the whole product.
   */
  #resetZoom = () => {
    // Reset scale and translation to defaults
    this.#scale = this.#initialScale;
    this.#startScale = this.#scale;
    this.#translate.x = 0;
    this.#translate.y = 0;

    // Reset gesture state to prevent interference on next zoom open
    this.#startPosition = { x: 0, y: 0 };
    this.#startTranslate = { x: 0, y: 0 };
    this.#isDragging = false;
    this.#lastTapTime = 0;
    this.#lastTapPosition = null;
    this.#hasDraggedBeyondThreshold = false;
    this.#activePointerId = null;
    this.#setInteracting(false);

    // Update CSS properties to reflect reset state
    this.style.setProperty('--drag-zoom-scale', this.#scale.toString());
    this.style.setProperty('--drag-zoom-translate-x', '0px');
    this.style.setProperty('--drag-zoom-translate-y', '0px');
    this.toggleAttribute('zoomed', this.#scale > MIN_ZOOM);
  };

  destroy() {
    this.#controller.abort();
    this.#cancelAnimationFrame();
  }
}

/**
 * Calculate distance between two points or touches
 * @param {Point | Touch} point1 - First point or touch
 * @param {Point | Touch} point2 - Second point or touch
 * @returns {number} Distance between the points
 */
function getDistance(point1, point2) {
  // Handle both Point objects (x, y) and Touch objects (clientX, clientY)
  const x1 = /** @type {Point} */ (point1).x ?? /** @type {Touch} */ (point1).clientX;
  const y1 = /** @type {Point} */ (point1).y ?? /** @type {Touch} */ (point1).clientY;
  const x2 = /** @type {Point} */ (point2).x ?? /** @type {Touch} */ (point2).clientX;
  const y2 = /** @type {Point} */ (point2).y ?? /** @type {Touch} */ (point2).clientY;

  const dx = x1 - x2;
  const dy = y1 - y2;
  return Math.sqrt(dx * dx + dy * dy);
}

if (!customElements.get('drag-zoom-wrapper')) {
  customElements.define('drag-zoom-wrapper', DragZoomWrapper);
}

/**
 * @typedef {Object} Point
 * @property {number} x
 * @property {number} y
 */

/**
 * @typedef {HTMLElement} ZoomDialogElement
 * @property {Function} close - Method to close the zoom dialog
 */
